'use client';

import { isWithinInterval } from 'date-fns';
import type { ReactNode } from 'react';

import { Link } from '~/components/Link';

export type Promo = {
  startDate: Date;
  endDate: Date;
  termsUrl: string;
  isPromoLive: boolean;
  isFreeUpgradePromo: boolean;
  freeUpgradeCopy?: ReactNode;
  checkboxLabel?: string;
};

export const CURRENT_PROMO = () => {
  // For simplicity, treats promo period as a free upgrade promo. Can refine to distinguish between pre-promo and promo periods as and if needed.
  const isFreeFirstClassUpgrade = true;

  // TODO: Update dates for each new promo campaign.
  // Dates use Date.UTC so the promo activates at the same absolute moment globally (midnight GMT)
  // JavaScript Date months are 0-indexed (0 = Jan, 1 = Feb, etc.)
  const startDate = new Date(Date.UTC(2026, 1, 9)); // 9th Feb 2026 00:00:00 UTC - banner go live
  const endDate = new Date(Date.UTC(2026, 2, 26, 23, 59, 59, 999)); // 26th March 2026 23:59:59.999 UTC - banner end
  const isPromoLive = isWithinInterval(new Date(), { start: startDate, end: endDate });

  return {
    startDate,
    endDate,
    isPromoLive,
    isFreeUpgradePromo: isPromoLive && isFreeFirstClassUpgrade,
    freeUpgradeCopy: (
      <>
        Free first-class travel pass upgrade is available for this trip for daytime trains that use a digital Interrail
        pass, subject to availability.{' '}
        <Link href="/firstclassupgradeterms26" isExternal hasUnderline>
          See T&Cs
        </Link>
        . Limited time only. Upgrade class varies across services,{' '}
        <Link href="/journal/first-class-train-travel" hasUnderline>
          read more
        </Link>
        .
      </>
    ),
    checkboxLabel: 'Upgrade all travellers (FREE)',
    termsUrl: '/firstclassupgradeterms26'
  };
};

export const HALF_PRICE_FIRST_CLASS_PROMO = () => {
  const startDate = new Date(Date.UTC(2026, 6, 1)); // 1st July 2026 00:00:00 UTC - promo go live
  const endDate = new Date(Date.UTC(2026, 6, 30, 23, 59, 59, 999)); // 30th July 2026 23:59:59.999 UTC - promo end
  const isPromoLive = isWithinInterval(new Date(), { start: startDate, end: endDate });

  return {
    startDate,
    endDate,
    isPromoLive
  };
};

// "Win a trip" referral competition banner. Kept independent of CURRENT_PROMO (the free
// first-class upgrade promo) so it only drives the GlobalBanner — it must NOT re-trigger the
// partner banners, navigation styling, checkout upgrade UI, or trip-card badges that gate on
// CURRENT_PROMO().isPromoLive.
export const COMPETITION_BANNER = () => {
  // Dates use Date.UTC so the banner activates at the same absolute moment globally.
  // JavaScript Date months are 0-indexed (5 = June).
  const startDate = new Date(Date.UTC(2026, 5, 8)); // 8th June 2026 00:00:00 UTC - banner go live
  const endDate = new Date(Date.UTC(2026, 5, 16, 23, 59, 59, 999)); // Ended 16th June 2026 — campaign disabled
  const isLive = isWithinInterval(new Date(), { start: startDate, end: endDate });

  return {
    startDate,
    endDate,
    isLive,
    url: 'https://www.byway.travel/about/refer'
  };
};

// First-class upgrade promo banner. Independent date-driven factory (mirrors COMPETITION_BANNER)
// so it only drives the GlobalBanner and never touches CURRENT_PROMO-gated UI.
export const FIRST_CLASS_PROMO_BANNER = () => {
  // Dates use Date.UTC so the banner activates at the same absolute moment globally.
  // JavaScript Date months are 0-indexed (6 = July).
  const startDate = new Date(Date.UTC(2026, 6, 1)); // 1st July 2026 00:00:00 UTC - banner go live
  const endDate = new Date(Date.UTC(2026, 6, 30, 23, 59, 59, 999)); // 30th July 2026 23:59:59.999 UTC - banner end
  const isLive = isWithinInterval(new Date(), { start: startDate, end: endDate });

  return {
    startDate,
    endDate,
    isLive,
    url: 'https://www.byway.travel/legal/first-class-upgrade-july26'
  };
};

export const PROMO_ID_FREE_FIRST_CLASS = 'interrail_eurail_free_first_class_upgrade';
const JAI_PROMO_NAME_FREE_UPGRADE = 'INTERRAIL_EURAIL_FREE_FIRST_CLASS_UPGRADE';
export const JAI_PROMO_NAME_HALF_PRICE_UPGRADE = 'INTERRAIL_EURAIL_HALF_PRICE_FIRST_CLASS_UPGRADE';

export function isBlueprintPromoEligible(eligiblePromotions: unknown): boolean {
  if (!Array.isArray(eligiblePromotions) || eligiblePromotions.length === 0) return false;
  return eligiblePromotions.includes(PROMO_ID_FREE_FIRST_CLASS);
}

export function hasAppliedFreeUpgradePromo(appliedPromotions: { name: string }[] | undefined): boolean {
  if (!appliedPromotions || appliedPromotions.length === 0) return false;
  return appliedPromotions.some((p) => p.name === JAI_PROMO_NAME_FREE_UPGRADE);
}
